/**
 * @voltxampere/components — PULSE voltage source (Sprint 22 B2)
 *
 * The independent voltage source whose value follows a SPICE-style
 * PULSE(V1, V2, TD, TR, TF, PW, PER) waveform:
 *
 *   t < TD                                → V = V1
 *   TD ≤ t < TD+TR                        → V1 ramps linearly to V2
 *   TD+TR ≤ t < TD+TR+PW                  → V = V2
 *   TD+TR+PW ≤ t < TD+TR+PW+TF            → V2 ramps linearly to V1
 *   TD+TR+PW+TF ≤ t < TD+PER              → V = V1
 *   t ≥ TD + PER                          → wraps with period PER
 *
 * Param defaults align with ngspice (V2 = 1, TR = TF = 1 ns,
 * PW = TD = 0, PER = 1 µs) so a fixture omitting timing parameters
 * still produces a sensible 1 µs / 50 % duty waveform.
 *
 * DC OP uses V = V1 (the value at t = 0). AC sweep also uses V1
 * (small-signal AC is 0 — the pulse is a transient-only stimulus).
 *
 * Pin layout mirrors `voltage-source` (`pos` top, `neg` bottom) so
 * the SPICE codec's two-terminal element template applies verbatim.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
export const PulseSourceParamsSchema = z
    .object({
    /** Value before TD and during the V1 plateau (V). */
    V1: z.number().finite().default(0),
    /** Value during the PW plateau (V). */
    V2: z.number().finite().default(1),
    /** Initial delay before the first rising edge (s). Non-negative. */
    TD: z.number().nonnegative().finite().default(0),
    /** Rise time V1 → V2 (s). Must be > 0 to keep the linear ramp well-defined. */
    TR: z.number().positive().finite().default(1e-9),
    /** Fall time V2 → V1 (s). Must be > 0. */
    TF: z.number().positive().finite().default(1e-9),
    /** Width of the V2 plateau (s). Non-negative. */
    PW: z.number().nonnegative().finite().default(0),
    /** Period of the pulse train (s). Must satisfy PER ≥ TR + PW + TF. */
    PER: z.number().positive().finite().default(1e-6),
})
    .strict()
    .superRefine((params, ctx) => {
    const minPer = params.TR + params.PW + params.TF;
    if (params.PER < minPer) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `PER (${String(params.PER)}) must be ≥ TR + PW + TF (${String(minPer)})`,
        });
    }
});
export const pulseSourceDefinition = {
    type: "pulse-source",
    category: "source",
    displayName: { tr: "Pulse Kaynağı", en: "Pulse Source" },
    symbol: "VPULSE",
    pins: [
        { id: "pos", side: "top", offset: 0, label: "+" },
        { id: "neg", side: "bottom", offset: 0, label: "-" },
    ],
    parameterSchema: PulseSourceParamsSchema,
    nonlinear: false,
};
//# sourceMappingURL=pulse-source.js.map